import React, { useEffect, useState } from 'react';
import DataGrid from 'devextreme-react/data-grid';
import StarIcon from '@markinson/uicomponents-v2/SvgIcons/Star';
import { withStyles } from '@material-ui/core/styles';
import styles from './Deliveries.styles';
import SummaryTable from 'components/common/SummaryTable';
import SummaryTableSchema from './SummaryTableSchema.json';
import Paper from '@material-ui/core/Paper';
import { IDeliveriesProps } from './Deliveries.properties';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import AddCircle from '@markinson/uicomponents-v2/SvgIcons/AddCircle';
import { useDeleteWorksaleDelivery, useRetrieveWorkSaleDeliveries, useUpdateDefaultWorksaleDelivery } from 'api/Worksale/worksale';
import DataSource from 'devextreme/data/data_source';
import { useSPContextSelector, useSPDispatch } from '../Worksale.context';
import Selectors from '../Worksale.selectors';
import { createDataSource, useGridKeyboardNavigation } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { ChangeCircle, PlaylistAddCircle } from '@markinson/uicomponents-v2/SvgIcons';
import ChangeDeliveryDialog from '../ChangeDeliveryDialog';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import { MAX_CONTAINER_WIDTH, SUMMARY_PANEL_WIDTH, columns } from './constants';
import { SalesProcessingActionsTypes } from '../Worksale.actions';
import { useElementSize } from 'utils/hooks';

const Deliveries = (props: IDeliveriesProps) => {
  const { classes, changeSelectedTab, changeConfirmationDialog, showDetailOnClick, onDefaultWorkSaleDeliveryUpdate, open, onClose } = props;
  const { onFocusedCellChanging } = useGridKeyboardNavigation();
  const worksaleId = useSPContextSelector(Selectors.WorksaleId) || -1;
  const customerId = useSPContextSelector(Selectors.CustomerId);
  const { mutateAsync: entriesFetchMutateAsync } = useRetrieveWorkSaleDeliveries();
  const { mutateAsync: updateDefaultWSDeliveryMutateAsync } = useUpdateDefaultWorksaleDelivery();
  const { mutateAsync: deleteWorksaleDeliveryMutateAsync } = useDeleteWorksaleDelivery();
  const dataGridRef = React.useRef<DataGrid>();
  const { setActionBar } = React.useContext(ActionBarContext);

  const [isFirstTime, setIsFirstTime] = React.useState<boolean>(true);

  const [selectedRow, setSelectedRow] = useState(null);
  const contextDispatch = useSPDispatch();
  const worksaleDeliveryId = useSPContextSelector(Selectors.WorksaleDeliveryId);
  React.useEffect(
    () => {
      contextDispatch({
        [SalesProcessingActionsTypes.setWorksaleDeliveryId]: undefined
      });
    },
    []
  );
  const deliveriesContainerRef = React.useRef<HTMLDivElement>(null);
  const { width: deliveriesContainerWidth } = useElementSize(deliveriesContainerRef);

  React.useEffect(
    () => {
      if (!customerId) {
        changeSelectedTab('Lines');

        return;
      }
      setActionBarButtons();
    },
    [changeSelectedTab, customerId, worksaleDeliveryId, selectedRow]
  );

  useEffect(() => setIsFirstTime(true), []);

  const setActionBarButtons = (): void => {
    setActionBar({
      leftIcons: [],
      centerIcons: [
        {
          label: 'Change Delivery',
          Icon: ChangeCircle,
          action: 'ChangeDelivery',
          disabled: !selectedRow?.LineCount
        },
        {
          label: 'Fulfillment',
          Icon: PlaylistAddCircle,
          action: 'Fulfillment',
          disabled: !worksaleDeliveryId
        }
      ],
      rightIcons: [
        {
          label: 'New Site',
          Icon: AddCircle,
          action: 'New_Site',
        }
      ]
    });
  };

  const fetchDeliveries = React.useCallback(
    async (BatchPage, BatchSize) => {
      if (worksaleId > 0 && customerId) {
        const response = await entriesFetchMutateAsync(
          {
            WorkSaleId: worksaleId,
            urlQueryParams: {
              BatchPage: BatchPage,
              BatchSize: BatchSize,
              Sort: '',
              DebId: customerId
            }
          }
        );
        if (!response.Status) {
          return [];
        }

        response.Deliveries = response?.Deliveries?.map((delivery, index) => ({
          ...delivery,
          DisableDeleteAction: !Number(delivery.WorkSaleDeliveryId),
          WorkSaleDeliveriesId: index + 1
        }));

        return response?.Deliveries;
      } else {
        return [];
      }
    },
    [worksaleId, customerId]
  );

  const updateDefaultDelivery = async (query) => {
    const data = {
      ...query,
      WorkSaleId: query.WorksaleId,
      urlQueryParams: {
        SiteCode: query.SiteCode,
      }
    };

    return updateDefaultWSDeliveryMutateAsync(data);
  };
  const deleteWorksaleDeliverySite = async (query) => {
    const data = {
      WorksaleDeliveryId: query
    };

    return deleteWorksaleDeliveryMutateAsync(data);
  };

  const dataSource = React.useMemo(
    () => createDataSource(
      'WorkSaleDeliveriesId',
      { fetch: fetchDeliveries },
    ),
    [fetchDeliveries]
  );

  const onSelectedRowChanged = ({ selectedRowsData }) => {
    const selectedRowData = (selectedRowsData && selectedRowsData[0]) ? selectedRowsData[0] : {};
    setSelectedRow(selectedRowData);
    contextDispatch({
      setWorksaleDeliveryId: Number(selectedRowData?.WorkSaleDeliveryId),
      setWorksaleDeliverySummary: selectedRowData,
    });
  };

  const handleFavorite = async ({ row }) => {
    const response = await updateDefaultDelivery({ SiteCode: (row.data && row.data) ? row.data.SiteCode : '', WorksaleId: worksaleId });
    onDefaultWorkSaleDeliveryUpdate(response);

    if (dataGridRef.current) {
      await dataGridRef.current.instance.refresh();
      const visibleRows = dataGridRef.current.instance.getVisibleRows();
      const defaultRowData = visibleRows.find((r) => r.data.Primary === true);
      const defaultRowIndex = defaultRowData?.rowIndex;
      if (defaultRowIndex !== undefined && defaultRowIndex !== null) {
        setSelectedRow(defaultRowData?.data);
        dataGridRef.current.instance.selectRowsByIndexes([defaultRowIndex]);
      }
    }
  };

  const removeRow = (_rowKey, rowData) => {
    changeConfirmationDialog({
      open: true,
      title: 'Delete Site',
      message: 'Are you sure you want to continue?',
      okLabel: 'Delete',
      onCancel: () => null,
      onOk: async () => {
        deleteWorksaleDeliverySite(rowData.WorkSaleDeliveryId)
          .finally(() => {
            dataGridRef.current.instance.refresh();
          });
      }
    });
  };

  const SelectAndFocusFirstRow = () => {
    setTimeout(
      () => {
        dataGridRef.current?.instance?.focus();
        dataGridRef?.current.instance.selectRowsByIndexes([0]);
        const data = dataGridRef?.current.instance.getSelectedRowsData();
        setSelectedRow(data?.[0]);
        contextDispatch({
          setWorksaleDeliveryId: Number(data[0]?.WorkSaleDeliveryId),
          setWorksaleDeliverySummary: data[0],
        });
      },
      0
    );
  };

  const onContentReady = React.useCallback(
    (e) => {
      if (isFirstTime && e.component.getDataSource()?.items().length) {
        SelectAndFocusFirstRow();
        setIsFirstTime(false);
      }
    },
    [isFirstTime]
  );

  const detailOnClick = (allProps) => {
    if (showDetailOnClick) {
      showDetailOnClick(allProps.data ? allProps.data : null);
    }
  };

  const actionsAtStart = (rowData) => (
    <a className={classes.anchor}>
      <StarIcon className={classes.actionButtonIcon} onClick={() => handleFavorite(rowData)} />
    </a>
  );

  return (
    <div className={classes.container} ref={deliveriesContainerRef}>
      <Paper className={classes.grid} style={{
        width: deliveriesContainerWidth <= MAX_CONTAINER_WIDTH
          ? 'calc(100% - 30px)'
          : `${deliveriesContainerWidth - SUMMARY_PANEL_WIDTH}px`
      }}
      >
        <DynamicDataGrid
          dataGridClassName={classes.dataGrid}
          onDelete={removeRow}
          columns={columns}
          allowHeaderFilting={false}
          allowUpdating={false}
          gridProps={{
            id: 'DeliveriesGrid',
            dataSource: dataSource || new DataSource([]),
            dataGridRef: dataGridRef,
            onContentReady: onContentReady,
            onSelectionChanged: onSelectedRowChanged,
            onFocusedCellChanging: onFocusedCellChanging,
            selection: { mode: 'single' },
            sorting: { mode: 'single' }
          }}
          detailOnClick={detailOnClick}
          actionsAtStart={actionsAtStart}
        />
      </Paper>
      {
        deliveriesContainerWidth > MAX_CONTAINER_WIDTH && (
          <SummaryTable
            loadingDeliverySummary={false}
            deliverySummary={selectedRow}
            data={
              SummaryTableSchema.Deliveries
            }
          />
        )
      }
      {open ?
        <ChangeDeliveryDialog
          open={open}
          dataSource={dataSource}
          SelectAndFocusFirstRow={SelectAndFocusFirstRow}
          onClose={onClose}
        /> : undefined}
    </div >
  );
};

export default withStyles(styles, { index: 1 })(Deliveries);
